import { ApiResponse } from 'apisauce';
import LangEnum from '../../lang/LangEnum';
import { Team } from '../../modules/context/ApplicationContext/applicationContext.types';
import { UsedInOfflineMode } from '../../modules/offline/types/UsedInOfflineMode';
import {
  ToggleShopping,
  ToggleShoppingKitParams,
} from '../../modules/orders/contexts/orderApplication.types';
import { OrderStatus } from '../../modules/orders/interfaces/order-status.interface';
import { Order, OrderTypeString, SynchronizedType } from '../../modules/orders/order.types';
import { Locales } from '../../modules/translation/messages';
import { TranslateType } from '../../modules/translation/translationContext.types';
import { UserRole } from '../../modules/users/roles';
import { IAffectedOrderProducts } from '../../services/modules/order/order.types';

export enum OrderBookStateEnum {
  INITIAL_STATE = 'INITIAL_STATE',
  SEARCH_CUSTOMER = 'SEARCH_CUSTOMER',

  LIST_ORDERS = 'LIST_ORDERS',
}

export interface SelectedOrderSummary {
  pieces: number;
  subtotal: number;
  total: number;
}

export type IStorageItems = Pick<
  OrderBookState,
  | 'orders'
  | 'selectedOrder'
  | 'selectedOrderSummary'
  | 'orderBookTimeline'
  | 'orderProductsIds'
  | 'otherOrdersProductsIds'
  | 'isOfflineMode'
>;

export type OrderBookStorage = Omit<IStorageItems, 'selectedOrder' | 'selectedOrderSummary'> & {
  selectedOrder?: OrderBookOrder | null;
  selectedOrderSummary?: SelectedOrderSummary | null;
};

export interface IOrdersProductIds {
  orderId: string;
  orderProductsIds: GetOrderProductIds;
}

interface OrderBookState extends UsedInOfflineMode {
  loading: boolean;
  loadingOrder: boolean;
  loadingOrderItems: boolean;
  isBlockOrderBook: boolean;
  customer?: OrderBookCustomer;
  orderBookState: boolean;
  orderBookModalState: boolean;
  orderBookTimeline: OrderBookStateEnum;
  selectedOrder?: OrderBookOrder;
  selectedOrderSummary?: SelectedOrderSummary;
  selectedOrderItemsQuantity: number;
  orderProductsIds: GetOrderProductIds;
  otherOrdersProductsIds?: IOrdersProductIds[];
  orders: OrderBookOrder[];
  lastCreatedOrderId?: string;
  orderAutocompleteFocused: boolean;
  salesOpportunityInterceptorId?: number;
  hideOrderValue: boolean;
}

export interface OrderBookCustomer {
  id?: string;
  priceTableId?: string;
  code?: string;
  commercialName?: string;
  companyName?: string;
  cpfcnpj?: string;
}

interface OrderBookPriceTable {
  id: string;
}

interface OrderBookBrand {
  id: string;
}

interface OrderBookSeller {
  id?: string;
  userId?: string;
}

interface OrderBookConsultant {
  id?: string;
}

interface OrderBookSalesRepresentative {
  id?: string;
}

export interface OrderBookOrder {
  id: string;
  code: string;
  customer?: OrderBookCustomer;
  status: OrderStatus;
  brand: OrderBookBrand;
  subTotal: number;
  totalDiscountValue: number;
  totalItems: number;
  totalPieces: number;
  totalValue: number;
  totalRetail: number;
  type: OrderTypeString;
  priceTable: OrderBookPriceTable;
  salesRepresentative?: OrderBookSalesRepresentative;
  consultant?: OrderBookConsultant;
  seller?: OrderBookSeller;
}

export interface RemoveOrderCatalogSnapshotParams {
  orderId: string;
  teamId?: string;
  isOfflineMode?: boolean;
}

export interface HandleChangeSelectedOrderParams {
  order: OrderBookOrder;
  translate?: TranslateType;
  teamId?: string | undefined;

  finishedOrder?: boolean;

  redirectOrder?: () => void;
}

export interface ICreateOrder {
  customerId?: string;
  isSuggested?: boolean;
  sellerId?: string;
  onSuccess?: (orderId: string) => void;
  translate?: TranslateType;
  team?: Team | undefined;
  history: any;
}

export interface ReloadOrderItemsQuantityBookParams {
  calculate: boolean;
  orderId: string;
}

export interface IsOrderActivableParams {
  role: UserRole;
  orderStatus: OrderStatus;
  team?: Team;
  synchronized?: SynchronizedType;
}

interface OrderBookActions {
  isShopping: (productColorId: string, orderProductsIds: GetOrderProductIds) => boolean;
  isOrderActivable: ({
    orderStatus,
    synchronized,
    role,
    team,
  }: IsOrderActivableParams) => boolean | undefined;
  toggleShopping: (params: ToggleShopping) => void;
  toggleShoppingKit: (params: ToggleShoppingKitParams) => void;
  fetchOrderProductIds: (orderId?: string) => Promise<GetOrderProductIds | undefined>;
  fetchOrder: (orderId: string) => Promise<ApiResponse<Order, Order>>;
  validateRehydratedOrders: () => Promise<void>;
  startLoadOrder: () => void;
  startLoadOrderItems: () => void;
  handleChangeBlockOrderBook: (flag: boolean) => void;
  handleOpenOrderBook: () => void;
  handleOpenOrderBookModal: () => void;
  handleCloseOrderBookModal: () => void;
  handleCloseOrderBook: () => void;
  reloadSelectedOrderSummary: () => Promise<void>;
  handleAddOrder: (order: Order) => void;
  toggleOrderValueVisibility: () => void;
  handleRemoveOrder: (orderId: string, teamId?: string) => void;
  handleRemoveSelectedOrder: (team?: Team) => void;
  handleRemoveAllOrders: (team?: Team) => void;
  handleChangeSelectedOrder: (params: HandleChangeSelectedOrderParams) => void;
  handleOrderAutocompleteFocused: (flag: boolean) => void;
  setOrderBookOfflineMode: (flag: boolean) => void;
  handleChangeOrderBookTimeline: (newState: OrderBookStateEnum, team?: Team) => void;
  handleFinishSelectedOrder: (teamId?: string) => void;
  createOrder: (params: ICreateOrder) => void;
  reloadOrderItemsQuantity: (
    calculate: boolean,
    orderId?: string,
    isSelectedOrder?: boolean,
  ) => Promise<IAffectedOrderProducts[] | undefined>;
  reloadOrderItemsQuantityBook: ({
    orderId,
    calculate,
  }: ReloadOrderItemsQuantityBookParams) => Promise<IAffectedOrderProducts[] | undefined>;
  updateSelectedOrderSummary: (summary: SelectedOrderSummary) => void;
  updateOtherOrdersProductsIds: (
    orderId: string,
    productIds: GetOrderProductIds,
  ) => IOrdersProductIds[];
  onChangeCustomer: (customer: OrderBookCustomer) => void;
  resetStore: (team?: Team) => void;
  handleChangeSalesOpportunityInterceptor: (id?: number) => void;
}

export type OrderBookStore = OrderBookState & OrderBookActions;

export interface GetOrderProductIds {
  productColorIds: string[];
  kitIds: string[];
}

export interface CalculateOrderBookCardDataParams {
  order?: OrderBookOrder;
  isSelectedOrder: boolean;
  selectedOrder?: OrderBookOrder;
  selectedOrderSummary?: SelectedOrderSummary;
  orderProductsIds: GetOrderProductIds;
  orderProductsInfo?: IOrdersProductIds;
  translate: (key: LangEnum, params?: Record<string, unknown>) => string;
  locale: Locales;
}
